import { ICheckedPaymentIntent, 
    ICreateLinkAccount, 
    ICreatedPaymentIntent, 
    ILinkedAccountResponse, 
    IRequestPaymentIntent, 
    IRetrievePaymentIntent, 
    IUserAccount 
} from "@interfaces/fc-payment/stripe";
import { IChangeSubscriptionData, ICreateSubscriptionData, ICreateSubscriptionResult, IStripeSubscriptionPrice, ISubscriptionVerificationId, IUserSubscription } from "@interfaces/fc-payment/subscriptions";
import fcPaymentServiceApiInstance from "@services/clients/fc-payment";
import stripeAccountServiceApiInstance from "@services/clients/stripe-account";

/**
 * Link account. Creates an account and links it to stripe
 * @param data Data required to link an account 
 * @returns Creation result. URL property is required in this result.
 */
export async function createLinkedAccount(data: ICreateLinkAccount): Promise<ILinkedAccountResponse>
{
    const endpoint = "/account/link"
    const response = await stripeAccountServiceApiInstance.post<ILinkedAccountResponse>(endpoint, data)
    return response.data
}


/**
 * Get current logged user's saved account
 * @returns User account
 */
export async function getUserAccount(organizationId?: number)
{
    const params = new URLSearchParams()
    if(organizationId)
        params.append("organizationId", organizationId.toString())
    const endpoint = `/account/user${organizationId ? "?" + params.toString() : ""}`;
    const result = await stripeAccountServiceApiInstance.get<IUserAccount>(endpoint);
    return result.data
}

/**
 * Get linked account basic information
 * @param accountId Target stripe account ID
 * @returns Account information
 */
export async function getAccountLink(accountId: string)
{
    const endpoint = `/account/${accountId}/links`
    const response = await stripeAccountServiceApiInstance.get(endpoint)
    return response.data
}

/**
 * Delete user account. It will enable the platform to link again to Stripe
 * @param accountId 
 * @returns 
 */
export async function deactivateAccount(accountId: string)
{
    const endpoint = `/account/${accountId}`
    const response = await stripeAccountServiceApiInstance.delete(endpoint)
    return response.status
}

export async function requestPayment(data: IRequestPaymentIntent)
{
    const endpoint = "/payment/intent";
    const result = await stripeAccountServiceApiInstance.post<ICreatedPaymentIntent>(endpoint, data);
    return result.data
}

export async function checkPayment(data: IRetrievePaymentIntent)
{
    const endpoint = "/payment/intent/check";
    const result = await stripeAccountServiceApiInstance.post<ICheckedPaymentIntent>(endpoint, data);
    return result.data
}

/**
 * Verifies if the linked Stripe account has been submitted in the Stripe Dashboard.
 * If not, then, it disables it.
 * @returns If true, the account has been submitted and no data changed, if false, then the account has been disabled
 */
export async function verifyAccountLinkSubmittedStatus(): Promise<boolean>
{
    const endpoint = "/account/validate";
    const result = await stripeAccountServiceApiInstance.put<boolean>(endpoint);
    return result.data
}

export async function getPrices(country?: string | null)
{
    const params = new URLSearchParams()
    if(country)
    {
        params.append("country", country)
    }
    const endpoint = `/subscription/prices?${params.toString()}`
    const result = await fcPaymentServiceApiInstance.get<IStripeSubscriptionPrice[]>(endpoint);
    return result.data
}

export async function getPlanPrice(plan: string, country?: string | null)
{
    const params = new URLSearchParams()
    if(country)
    {
        params.append("country", country)
    }
    const endpoint = `/subscription/price/${plan}?${params.toString()}`
    const result = await fcPaymentServiceApiInstance.get<IStripeSubscriptionPrice>(endpoint);
    return result.data
}

/**
 * Create a new stripe subscription.
 */
export async function createSubscriptionOrder(data: ICreateSubscriptionData, country?: string | null)
{
    const params = new URLSearchParams()
    if(country)
    {
        params.append("country", country)
    }
    const endpoint = `/subscription/stripe/?${params.toString()}`
    const result = await fcPaymentServiceApiInstance.post<ICreateSubscriptionResult>(endpoint, data);
    return result.data
}

export async function verifySubscription()
{
    
    const endpoint = "/subscription/verify"
    const result = await fcPaymentServiceApiInstance.put<ISubscriptionVerificationId>(endpoint, {});
    return result.data
}

export async function getUserSubscription(): Promise<IUserSubscription>
{
    const endpoint = "/subscription/user"
    const result = await fcPaymentServiceApiInstance.get<IUserSubscription>(endpoint);
    return result.data
}

export async function getSubscriptionInfo(subscriptionId: string)
{
    const endpoint = `/subscription/info/${subscriptionId}`
    const result = await fcPaymentServiceApiInstance.get<IStripeSubscriptionPrice>(endpoint);
    return result.data
}

/**
 * This will return a result like the `createSubscriptionOrder` function, but this should be called if 
 * the current logged user already has a payed (including free) stripe subscription 
 * (Do not call for trial or coupon upgrade).
 */
export async function changeSubscription(data: IChangeSubscriptionData)
{
    const endpoint = "/subscription/change/stripe"
    const result = await fcPaymentServiceApiInstance.put<ICreateSubscriptionResult>(endpoint, data);
    return result.data
}

export async function cancelSubscription()
{
    const endpoint = "/subscription/user"
    const result = await fcPaymentServiceApiInstance.delete<ICreateSubscriptionResult>(endpoint);
    return result.status
}
